import { Track } from "@/src/lib/tracking";
import Cookies from "js-cookie";
import {
  LOCALES,
  type Locale,
  isValidLocale as isValidLocaleUtil,
} from "./i18n.locale";
import { PathHandler } from "./proxyclass/PathHandler";

export interface LanguageOption {
  readonly label: string;
  readonly value: Locale;
  readonly code: string;
}

// 导出常量以保持向后兼容
const { EN, ZHTW } = LOCALES;

export const locales: readonly Locale[] = Object.values(LOCALES);

export const languageOptions: readonly LanguageOption[] = [
  // 英语（默认）
  { label: "English", value: EN, code: "en-US" },
  // 繁体中文（使用繁体汉字）
  { label: "中文（繁體）", value: ZHTW, code: "zh-TW" },
] as const;

/**
 * 模块化翻译接口定义
 */
interface ModuleTranslations {
  [locale: string]: { [key: string]: string };
}

/**
 * 插值变量类型定义
 */
type InterpolationVariables = Record<string, any>;

/**
 * I18n 存储 - 全局 locale 存储对象
 */
const i18nStorage = {
  currentLocale: "en" as string,

  /**
   * 设置 locale
   * @param locale 语言代码
   */
  setLocale(locale: string): void {
    this.currentLocale = locale;
  },

  /**
   * 获取当前存储的 locale
   * @returns 语言代码，默认返回 "en"
   */
  getLocale(): string {
    return this.currentLocale;
  },
};

/**
 * I18n 工具类 - 从 URL 路径获取语言（适配 Next.js）
 */
class I18nUtil {
  /**
   * 创建翻译实例
   * @param translations 模块翻译数据
   * @param locale 可选的直接传入语言代码（优先级高于自动检测）
   * @returns 翻译实例，包含 t 方法
   */
  static createScoped(translations: ModuleTranslations, _locale?: string) {
    return {
      t: (key: string, options?: InterpolationVariables): string => {
        let currentLocale: string = _locale || i18nStorage.getLocale();
        if (typeof window !== "undefined") {
          try {
            const cookieLocale = Cookies.get("locale");
            if (cookieLocale && cookieLocale !== currentLocale) {
              currentLocale = cookieLocale as string;
            }
          } catch (error) {
            Track.log("warn", "i18n", "Error getting locale from cookie:", {
              cause: String(error),
            });
          }
        }

        if (options?.locale) {
          const { locale: optionsLocale, ...newOptions } = options;
          return this.translate(
            translations,
            currentLocale,
            key,
            newOptions,
            optionsLocale,
          );
        }
        return this.translate(translations, currentLocale, key, options);
      },
    };
  }

  /**
   * 获取当前语言 - 客户端专用
   * @returns 语言代码，默认 'en'
   *
   */
  static getCurrentLocale(): string {
    // 仅在客户端环境下工作
    if (typeof window === "undefined") {
      return "en";
    }

    // 1. 优先从 Cookie 获取
    const cookieLocale = this.getLocaleFromCookie();
    if (cookieLocale) {
      return cookieLocale;
    }

    // 2. 从 URL 路径获取
    const urlLocale = this.getClientLocale();
    if (this.isValidLocale(urlLocale)) {
      return urlLocale;
    }

    // 3. 默认返回英语
    return "en";
  }

  /**
   * 客户端获取语言代码
   * @returns 语言代码
   */
  private static getClientLocale(): string {
    try {
      if (typeof window === "undefined") {
        return "en";
      }
      const pathname = window?.location?.pathname;
      const segments = pathname.split("/").filter(Boolean);

      // /app/:locale
      if (
        segments[0] === "app" &&
        segments.length === 2 &&
        this.isValidLocale(segments[1])
      ) {
        return segments[1];
      }
      // /app
      if (segments[0] === "app" && segments.length === 1) {
        return "en";
      }
      // /:locale/xxx 或 /:locale
      if (segments.length > 0 && this.isValidLocale(segments[0])) {
        return segments[0];
      }
      // 其它情况
      return "en";
    } catch (error) {
      Track.log("warn", "i18n", "Error getting locale from URL path:", {
        cause: String(error),
      });
      return "en";
    }
  }

  /**
   * 统一的翻译方法
   * @param translations 翻译数据
   * @param locale 语言代码
   * @param key 翻译键
   * @param options 插值选项
   * @param targetLocale 目标语言代码
   * @returns 翻译后的文本
   */
  private static translate(
    translations: ModuleTranslations,
    locale: string,
    key: string,
    options?: InterpolationVariables,
    targetLocale?: string,
  ): string {
    const currentLocale = targetLocale || locale;
    // 获取当前语言的翻译
    const currentTranslations = translations[currentLocale];
    let translation = currentTranslations?.[key];

    // 如果当前语言没有翻译，回退到英文
    if (!translation && currentLocale !== "en") {
      translation = translations["en"]?.[key];
    }

    // 如果还是没有翻译，使用原文案
    if (!translation) {
      translation = key;
    }

    // 处理变量插值
    if (options && typeof translation === "string") {
      return this.interpolateVariables(translation, options);
    }

    return translation;
  }

  /**
   * 切换语言（设置 Cookie 并通过路由跳转）
   * @param newLocale 新的语言代码
   */
  static switchLocale(newLocale: string): string | null {
    if (!this.isValidLocale(newLocale)) {
      Track.log("warn", "i18n", `Invalid locale: ${newLocale}`);
      return null;
    }

    try {
      Cookies.set("locale", newLocale, {
        expires: 365,
        path: "/",
        sameSite: "Lax",
        secure: location.protocol === "https:",
      });

      const currentPath = window.location.pathname;
      const newPath = PathHandler.buildPathWithLocale(currentPath, newLocale);
      const { search, hash } = window.location;
      const newUrl = newPath + search + hash;

      return newUrl;
    } catch (error) {
      Track.log("error", "i18n", "Failed to switch locale:", {
        cause: String(error),
      });
      return null;
    }
  }

  /**
   * 从 Cookie 获取语言偏好
   * @returns 语言代码或 null
   */
  static getLocaleFromCookie(): string | null {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      const cookieLocale = Cookies.get("locale");
      return cookieLocale && this.isValidLocale(cookieLocale)
        ? cookieLocale
        : null;
    } catch (error) {
      Track.log("warn", "i18n", "Error getting locale from cookie:", {
        cause: String(error),
      });
      return null;
    }
  }

  /**
   * 变量插值处理
   * @param template 模板字符串
   * @param variables 变量对象
   * @returns 插值后的字符串
   */
  static interpolateVariables(
    template: string,
    variables: InterpolationVariables,
  ): string {
    return template.replace(/%\{(\w+)\}/g, (match, key) => {
      const value = variables[key];
      return value !== undefined ? String(value) : match;
    });
  }

  /**
   * 获取支持的语言列表
   */
  static getSupportedLocales(): Locale[] {
    return Object.values(LOCALES);
  }

  /**
   * 检查是否为有效的语言代码
   */
  static isValidLocale(locale: string): locale is Locale {
    return isValidLocaleUtil(locale);
  }

  /**
   * 获取语言选项列表（用于语言切换器）
   */
  static getLanguageOptions(): readonly LanguageOption[] {
    return languageOptions;
  }

  /**
   *  用于将后端的key转换为前端key
   */
  static getFrontEndLanguageKkey(locale: string) {
    return languageOptions.find((item) => item.code === locale)?.value || "en";
  }

  /**
   * 用于将前端的key转换为后端key
   */
  static getBackgroundLanguageKey(locale: string) {
    return (
      languageOptions.find((item) => item.value === locale)?.code || "en-US"
    );
  }
}

export { I18nUtil, i18nStorage };
